import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, GitPullRequest, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { apiMessage } from '@/lib/pricing';

export type Artifact = { kind: string; name: string; digest: string; reference: string };

export type Release = {
  version: string;
  published_at: string;
  channel: string;
  pull_request: number | null;
  git_sha: string;
  migration_head: string;
  complete: boolean;
  artifacts: Artifact[];
  direction?: 'installed' | 'newer' | 'older' | 'unknown';
  changed?: string[];
  migration_state_known?: boolean;
};

export type Adoption = {
  id: string;
  version: string;
  pull_request: number | null;
  from_version: string | null;
  kind: string;
  status: string;
  status_label: string;
  requested_by: string;
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
  message: string;
  progress: number;
  error: string;
  kept_running: string;
  unmigrate: [string, string][];
  runner: string;
  quiet: boolean;
  log: string[];
};

export type UpdatesState = {
  installed: Release | null;
  policy: {
    follow_latest: boolean;
    pinned_version: string | null;
    release_channels: string[];
    changed_by: string;
    changed_at: string | null;
  };
  target: Release | null;
  behind: number;
  ahead_of_public: boolean;
  updater: { name: string | null; runtime: string | null; seen_at: string | null };
  has_upstream: boolean;
  active: Adoption | null;
  releases: Release[];
  history: Adoption[];
};

type Plan = {
  version: string;
  downgrade: boolean;
  known: boolean;
  reason: string;
  apps: { app: string; migrations: string[] }[];
  irreversible: { app: string; migration: string; reason: string }[];
  refusal: string;
};

const BASE = '/global-admin/aoc-updates/';

const api = () => authenticatedBitswanBackendInstance();

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const RUNTIME_LABEL: Record<string, string> = {
  compose: 'Docker Compose, through the on-prem CLI agent',
  k3s: 'k3s, through the on-prem CLI agent',
  kubernetes: 'Kubernetes, through the updater deployment',
};

function ReleaseName({ release }: { release: { version: string; pull_request: number | null } }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="font-mono">{release.version}</span>
      {release.pull_request ? (
        <Badge variant="outline" className="gap-1 border-violet-200 bg-violet-50 text-violet-700">
          <GitPullRequest className="h-3 w-3" />
          Pull request #{release.pull_request}
        </Badge>
      ) : null}
    </span>
  );
}

function Contents({ release }: { release: Release }) {
  return (
    <div className="mt-3 space-y-1 rounded-md bg-muted/50 p-3 text-xs">
      {release.git_sha ? <div>Built from commit <span className="font-mono">{release.git_sha.slice(0, 12)}</span></div> : null}
      {release.migration_head ? (
        <div>Last database migration: <span className="font-mono">{release.migration_head}</span></div>
      ) : null}
      {release.migration_state_known === false ? (
        <div className="text-amber-700">
          This release does not record its database migrations, so it cannot be rolled back to.
        </div>
      ) : null}
      {release.changed && release.changed.length > 0 && release.direction !== 'installed' ? (
        <div>Differs from what runs now in: {release.changed.join(', ')}</div>
      ) : null}
      <ul className="mt-2 space-y-1">
        {release.artifacts.map((artifact) => (
          <li key={artifact.name} className="break-all">
            <span className="font-medium">{artifact.name}</span>{' '}
            <span className="text-muted-foreground">({artifact.kind})</span>{' '}
            <span className="font-mono text-muted-foreground">{artifact.digest.slice(0, 19)}…</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Progress({ active }: { active: Adoption }) {
  const percent = Math.round(Math.max(0.05, active.progress) * 100);
  return (
    <Card className="border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Loader2 className="h-4 w-4 animate-spin" />
          {active.kind === 'rollback' ? 'Rolling back' : 'Updating'} to <ReleaseName release={active} />
        </CardTitle>
        <CardDescription>
          {active.status_label}. {active.message}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="h-2 w-full overflow-hidden rounded bg-muted">
          <div className="h-full bg-blue-500 transition-all" style={{ width: `${percent}%` }} />
        </div>
        {active.unmigrate.length > 0 ? (
          <p className="text-sm">
            Database migrations are reversed first, with the code that runs now:{' '}
            {active.unmigrate.map(([app, target]) => `${app} back to ${target}`).join(', ')}.
          </p>
        ) : null}
        {active.quiet ? (
          <p className="text-sm text-amber-700">
            The updater has not reported for more than 15 minutes. Check that it is still running on the machine
            or in the cluster.
          </p>
        ) : null}
        {active.log.length > 0 ? (
          <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-2 text-xs">
            {active.log.join('\n')}
          </pre>
        ) : null}
      </CardContent>
    </Card>
  );
}

function LastFailure({ failed }: { failed: Adoption }) {
  return (
    <Card className="border-red-200 bg-red-50/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-red-800">
          <AlertTriangle className="h-4 w-4" />
          The update to {failed.version} failed
        </CardTitle>
        <CardDescription className="text-red-800">{failed.error}</CardDescription>
      </CardHeader>
      <CardContent className="text-sm">
        {failed.kept_running
          ? `This AOC was left running ${failed.kept_running}.`
          : 'Nothing was changed on this AOC.'}
      </CardContent>
    </Card>
  );
}

export function AocUpdatesView({
  state,
  busy,
  onApply,
  onFollow,
  onChannels,
  onPlan,
}: {
  state: UpdatesState;
  busy: boolean;
  onApply: (version: string, allowDowngrade: boolean) => void;
  onFollow: () => void;
  onChannels: (channels: string[]) => void;
  onPlan: (version: string) => Promise<Plan>;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [confirm, setConfirm] = useState<{ release: Release; plan: Plan | null; loading: boolean } | null>(null);
  const [confirmFollow, setConfirmFollow] = useState(false);
  const followsPullRequests = state.policy.release_channels.includes('pull-request');
  const latestFailure = state.history.find((h) => h.status === 'failed');
  const showFailure = !state.active && latestFailure && state.history[0]?.id === latestFailure.id;
  const blocked = busy || !!state.active;

  const askRollback = async (release: Release) => {
    setConfirm({ release, plan: null, loading: true });
    try {
      const plan = await onPlan(release.version);
      setConfirm({ release, plan, loading: false });
    } catch (err) {
      toast.error(apiMessage(err));
      setConfirm(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Updates</h1>
        <p className="text-sm text-muted-foreground">
          Which release of the AOC this instance runs, and moving it to another one. This is the AOC itself, not the
          Bailey servers it serves.
        </p>
      </div>

      {state.active ? <Progress active={state.active} /> : null}
      {showFailure && latestFailure ? <LastFailure failed={latestFailure} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">On this AOC</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <div className="text-muted-foreground">Installed release</div>
            <div className="mt-1">
              {state.installed ? (
                <ReleaseName release={state.installed} />
              ) : (
                'The running images match no known release'
              )}
            </div>
            {state.behind > 0 ? (
              <div className="mt-1 text-amber-700">
                {state.behind} newer release{state.behind === 1 ? '' : 's'} available
              </div>
            ) : state.installed ? (
              <div className="mt-1 text-emerald-700">No newer release is available</div>
            ) : null}
          </div>
          <div>
            <div className="text-muted-foreground">Release setting</div>
            <div className="mt-1">
              {state.policy.follow_latest ? (
                'Follows the latest public release'
              ) : state.policy.pinned_version ? (
                <span>
                  Stays on <span className="font-mono">{state.policy.pinned_version}</span>
                </span>
              ) : (
                'Not set: nothing is updated until a release is chosen'
              )}
            </div>
            {state.policy.follow_latest && state.ahead_of_public ? (
              <div className="mt-1 text-muted-foreground">
                The installed release is newer than the latest public one. It stays installed until a newer public
                release appears; following never moves this AOC backwards.
              </div>
            ) : null}
            {state.policy.changed_by ? (
              <div className="mt-1 text-xs text-muted-foreground">
                Set by {state.policy.changed_by} {when(state.policy.changed_at)}
              </div>
            ) : null}
          </div>
          <div>
            <div className="text-muted-foreground">Updater</div>
            <div className="mt-1">
              {state.updater.name ? (
                <span>
                  {RUNTIME_LABEL[state.updater.runtime ?? ''] ?? state.updater.runtime}, last seen{' '}
                  {when(state.updater.seen_at)}
                </span>
              ) : (
                <span className="text-amber-700">
                  No updater has checked in, so a chosen release is not applied yet. On a machine installed with
                  the on-prem CLI, run <span className="font-mono">bitswan-on-prem-aoc agent --install</span>.
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              disabled={blocked || state.policy.follow_latest}
              onClick={() => setConfirmFollow(true)}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Follow the latest public release
            </Button>
            <label className="flex items-start gap-3">
              <Switch
                checked={followsPullRequests}
                disabled={busy}
                onCheckedChange={(on) => onChannels(on ? ['public', 'pull-request'] : ['public'])}
                aria-label="Follow pull requests"
              />
              <span>
                <span className="font-medium">Follow pull requests</span>
                <span className="block text-xs text-muted-foreground">
                  List the release each pull request built, so it can be tried here with one click. Following the
                  latest public release never installs one on its own.
                  {state.has_upstream
                    ? ' The upstream AOC must also offer pull request releases to this AOC’s licence.'
                    : ''}
                </span>
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Releases</CardTitle>
          <CardDescription>Newest first. Open a release to see what it contains.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {state.releases.length === 0 ? (
            <p className="text-sm text-muted-foreground">This AOC knows of no releases yet.</p>
          ) : null}
          {state.releases.map((release) => (
            <div key={release.version} className="rounded-md border p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  className="flex items-start gap-2 text-left text-sm"
                  onClick={() => setOpen({ ...open, [release.version]: !open[release.version] })}
                  aria-expanded={!!open[release.version]}
                >
                  {open[release.version] ? (
                    <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />
                  )}
                  <span>
                    <ReleaseName release={release} />
                    <span className="block text-xs text-muted-foreground">
                      {release.channel === 'public' ? 'Public' : 'Pull request'} release, published{' '}
                      {when(release.published_at)}
                    </span>
                  </span>
                </button>
                <div className="flex items-center gap-2">
                  {release.direction === 'installed' ? (
                    <Badge className="bg-emerald-600">Installed</Badge>
                  ) : release.direction === 'older' ? (
                    <Button size="sm" variant="outline" disabled={blocked} onClick={() => askRollback(release)}>
                      Roll back
                    </Button>
                  ) : (
                    <Button size="sm" disabled={blocked} onClick={() => onApply(release.version, false)}>
                      {release.pull_request ? 'Try this pull request' : 'Update to'}
                    </Button>
                  )}
                </div>
              </div>
              {open[release.version] ? <Contents release={release} /> : null}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Update history</CardTitle>
        </CardHeader>
        <CardContent>
          {state.history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No updates recorded yet.</p>
          ) : (
            <ul className="divide-y text-sm">
              {state.history.map((entry) => (
                <li key={entry.id} className="py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant="outline"
                      className={
                        entry.status === 'done'
                          ? 'border-emerald-200 text-emerald-700'
                          : entry.status === 'failed'
                            ? 'border-red-200 text-red-700'
                            : 'border-blue-200 text-blue-700'
                      }
                    >
                      {entry.status_label}
                    </Badge>
                    <span>
                      {entry.from_version ?? 'unknown'} to <ReleaseName release={entry} />
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {entry.kind === 'rollback' ? 'Roll back' : 'Update'} by {entry.requested_by},{' '}
                    {when(entry.requested_at)}
                    {entry.finished_at ? `, finished ${when(entry.finished_at)}` : ''}
                  </div>
                  {entry.error ? <div className="mt-1 text-xs text-red-700">{entry.error}</div> : null}
                  {entry.kept_running && entry.status === 'failed' ? (
                    <div className="text-xs text-muted-foreground">Left running: {entry.kept_running}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmFollow} onOpenChange={setConfirmFollow}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Follow the latest public release?</AlertDialogTitle>
            <AlertDialogDescription>
              This AOC will move to each new public release as it appears. It never moves backwards on its own: if
              the installed release is newer than the latest public one, it stays installed until a newer public
              release appears.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              onClick={() => {
                setConfirmFollow(false);
                onFollow();
              }}
            >
              Follow the latest public release
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirm} onOpenChange={(value) => (value ? null : setConfirm(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Roll back to {confirm?.release.version}?</AlertDialogTitle>
            <AlertDialogDescription>
              This moves the AOC to an older release than the one installed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirm?.loading ? (
            <p className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Working out what this does to the database…
            </p>
          ) : confirm?.plan ? (
            <div className="space-y-3 text-sm">
              {confirm.plan.refusal ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800">{confirm.plan.refusal}</div>
              ) : confirm.plan.apps.length === 0 ? (
                <p>No database migration has to be undone. The older images can run against the database as it is.</p>
              ) : (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                  <p>
                    These database migrations are undone first, with the code that runs now. If undoing them fails,
                    the installed release keeps running. Data in the columns and tables they added is lost.
                  </p>
                  <ul className="mt-2 space-y-1">
                    {confirm.plan.apps.map((group) => (
                      <li key={group.app}>
                        <span className="font-medium">{group.app}:</span>{' '}
                        <span className="break-all font-mono text-xs">{group.migrations.join(', ')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={!confirm?.plan || !!confirm.plan.refusal}
              onClick={() => {
                if (confirm) onApply(confirm.release.version, true);
                setConfirm(null);
              }}
            >
              Roll back
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AocUpdatesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<UpdatesState>({
    queryKey: ['aoc-updates'],
    queryFn: async () => (await (await api()).get(BASE)).data,
    refetchInterval: (query) => (query.state.data?.active ? 3000 : 30000),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['aoc-updates'] });
  const apply = useMutation({
    mutationFn: (body: { version: string; allow_downgrade: boolean }) =>
      api().then((client) => client.post(`${BASE}apply/`, body)),
    onSuccess: () => {
      toast.success('The update is queued. The updater picks it up within a minute.');
      refresh();
    },
    onError: (err) => toast.error(apiMessage(err)),
  });
  const follow = useMutation({
    mutationFn: () => api().then((client) => client.post(`${BASE}follow/`, {})),
    onSuccess: () => refresh(),
    onError: (err) => toast.error(apiMessage(err)),
  });
  const channels = useMutation({
    mutationFn: (release_channels: string[]) =>
      api().then((client) => client.put(`${BASE}channels/`, { release_channels })),
    onSuccess: () => refresh(),
    onError: (err) => toast.error(apiMessage(err)),
  });

  if (isLoading) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  if (error || !data) return <p className="p-6 text-sm text-red-700">{apiMessage(error)}</p>;
  return (
    <AocUpdatesView
      state={data}
      busy={apply.isPending || follow.isPending || channels.isPending}
      onApply={(version, allowDowngrade) => apply.mutate({ version, allow_downgrade: allowDowngrade })}
      onFollow={() => follow.mutate()}
      onChannels={(value) => channels.mutate(value)}
      onPlan={async (version) => (await (await api()).post(`${BASE}plan/`, { version })).data}
    />
  );
}
